import { FilterNamesEnum } from '@/client/helpers';
import {
  CourseCompletionReportsType,
  userAwarenessReportsType,
} from '@/client/reports';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { KeyResultCardData } from '@/components/courses/KeyResultCard';

export const getTrainingCompletionCard = (
  data: CourseCompletionReportsType | undefined,
  t: TranslationFunctionType,
): KeyResultCardData => ({
  title: t('awareness.keyResults.trainingCompletion'),
  tooltip: t('awareness.keyResults.trainingCompletion.tooltip'),
  chartId: 'key-result-training-completion',
  rows: [
    {
      label: t('awareness.keyResults.totalEnrollments'),
      value: data?.totalEnrolments ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_HAVE_ENROLLED_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
    {
      label: t('awareness.keyResults.totalCompletions'),
      value: data?.totalCompletions ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_HAVE_ENROLLED_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
  ],
  doughnutNumerator: data?.totalCompletions ?? 0,
  doughnutDenominator: data?.totalEnrolments ?? 0,
});

export const getTrainingContentCard = (
  data: CourseCompletionReportsType | undefined,
  t: TranslationFunctionType,
): KeyResultCardData => ({
  title: t('awareness.keyResults.trainingContent'),
  tooltip: t('awareness.keyResults.trainingContent.tooltip'),
  chartId: 'key-result-training-content',
  rows: [
    {
      label: t('awareness.keyResults.availableTrainings'),
      value: data?.availableCourses ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.COURSES](),
      },
    },
    {
      label: t('awareness.keyResults.enrolledTrainings'),
      value: data?.enrolledCourses ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_SCHEDULE](),
      },
    },
  ],
  doughnutNumerator: data?.enrolledCourses ?? 0,
  doughnutDenominator: data?.availableCourses ?? 0,
});

export const getUserCompletionCard = (
  data: userAwarenessReportsType | undefined,
  t: TranslationFunctionType,
): KeyResultCardData => ({
  title: t('awareness.keyResults.userCompletion'),
  tooltip: t('awareness.keyResults.userCompletion.tooltip'),
  chartId: 'key-result-user-completion',
  rows: [
    {
      label: t('awareness.keyResults.haveCompletedAllTraining'),
      value: data?.usersWithCompletedCourses ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_COMPLETED_ALL_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
    {
      label: t('awareness.keyResults.haveNotCompletedAllTraining'),
      value: data?.usersWithoutCompletedCourses ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_COMPLETED_ALL_COURSES]: 'no' },
        isDashboardLink: true,
      },
    },
  ],
  doughnutNumerator: data?.usersWithCompletedCourses ?? 0,
  doughnutDenominator:
    Number(data?.usersWithCompletedCourses ?? 0) +
    Number(data?.usersWithoutCompletedCourses ?? 0),
});

export const getOverdueUsersCard = (
  data: userAwarenessReportsType | undefined,
  t: TranslationFunctionType,
): KeyResultCardData => ({
  title: t('awareness.keyResults.overdueUsers'),
  tooltip: t('awareness.keyResults.overdueUsers.tooltip'),
  chartId: 'key-result-overdue-users',
  rows: [
    {
      label: t('awareness.keyResults.haveOverdueTraining'),
      value: data?.usersWithOverDueCoursesAndMaterials ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.SUMMARY_HAVE_OVERDUE_COURSES]: 'yes' },
        isDashboardLink: true,
      },
    },
    {
      label: t('awareness.keyResults.haveNeverLoggedIn'),
      value: data?.usersNeverLoggedIn ?? 0,
      page: {
        to: RedirectPaths[RedirectPathsEnum.REPORTS_SUMMARY](),
        filters: { [FilterNamesEnum.NEVER_LOGGED_IN]: 'yes' },
        isDashboardLink: true,
      },
    },
  ],
  doughnutNumerator: data?.usersWithOverDueCoursesAndMaterials ?? 0,
  doughnutDenominator: data?.usersWithEnrolledCourses ?? 0,
});
